import {
  Box,
  Dialog,
  Divider,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { generateClient } from 'aws-amplify/api';

import { Icon } from '@iconify/react';
import { Schema } from '../../../../../amplify/data/resource';

import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FundLogo } from '../../../../components/custom/FundLogo';
import CoinIcon from '../../../../components/icons/CoinIcon';
import { formatMoney } from '../../../../utils/format/format';

import ReactLoading from 'react-loading';

const apiClient = generateClient<Schema>();

// @ts-ignore
type Payment = Schema['AgentPaymentLink']['type'];

const Link = ({
  open,
  onClose,
  paymentId,
}: {
  open: boolean;
  onClose: () => void;
  paymentId: string;
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState<boolean>(false);
  const [payment, setPayment] = useState<Payment | null>(null);
  const [paymentLink, setPaymentLink] = useState<string>('');
  const [paymentLinkValid, setPaymentLinkValid] = useState<boolean>(false);
  const [clientName, setClientName] = useState<string>('');
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [isLinkExpired, setIsLinkExpired] = useState<boolean>(false);
  const [showLink, setShowLink] = useState<boolean>(false);

  useEffect(() => {
    const fetchPaymentLink = async () => {
      setLoading(true);
      try {
        const response = await apiClient.models.AgentPaymentLink.get({
          id: paymentId,
        });

        console.log('response', response);
        setPayment(response.data as Payment | null);
        setPaymentLink(response.data?.link || '');
        setClientName(response.data?.clientFullName || '');

        if (response.data?.validUntil) {
          const validUntil = new Date(response.data.validUntil).getTime();
          const now = new Date().getTime();

          if (validUntil > now) {
            setPaymentLinkValid(true);
            setIsLinkExpired(false);

            const remainingTime = Math.floor((validUntil - now) / 1000);
            setTimeLeft(remainingTime);
          } else {
            setPaymentLinkValid(false);
            setIsLinkExpired(true);
            setTimeLeft(0);
          }
        }

        setShowLink(false);

        setLoading(false);
      } catch (error) {
        console.error('Error fetching payment link:', error);
        enqueueSnackbar('Ошибка при загрузке данных платежа', {
          variant: 'error',
        });
        setLoading(false);
      }
    };

    if (open && paymentId) {
      fetchPaymentLink();
    }

    if (!open) {
      setShowLink(false);
    }
  }, [paymentId, open, enqueueSnackbar]);

  const handleApprove = () => {
    handleRestartLink();
  };

  const handleCancel = () => {
    onClose();
  };

  const handleRestartLink = async () => {
    setLoading(true);
    try {
      const response = await apiClient.queries.generateAgentPaymentLink({
        clientId: payment?.clientId || '',
        fund: payment?.fund || '',
        agentId: payment?.agentId || '',
        userId: payment?.userId || '',
        clientBank: payment?.clientBank || '',
        clientBranch: payment?.clientBranch || '',
        clientAccount: payment?.clientAccount || '',
        clientFullName: payment?.clientFullName || '',
        paymentAmount: payment?.paymentAmount || '',
        polisaNumber: payment?.polisaNumber || '',
        description: payment?.description || '',
        restartLink: true,
        paymentId: paymentId,
      });

      const parsedData =
        typeof response.data === 'string'
          ? JSON.parse(response.data)
          : response.data;

      if (!parsedData?.body || typeof parsedData.body !== 'object') {
        throw new Error('invalid_format');
      }

      const { paymentLink } = parsedData.body;

      if (!paymentLink) {
        throw new Error('missing_link');
      }

      setPaymentLink(paymentLink);
      setPaymentLinkValid(true);
      setIsLinkExpired(false);

      setTimeLeft(15 * 60);

      setShowLink(true);

      setLoading(false);
    } catch (error) {
      console.error('Error restarting payment link:', error);
      enqueueSnackbar('Error', {
        variant: 'error',
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    let timerId: NodeJS.Timeout;

    if (open && timeLeft !== null && timeLeft > 0) {
      timerId = setInterval(() => {
        setTimeLeft((prevTimeLeft) => {
          if (prevTimeLeft === null || prevTimeLeft <= 1) {
            clearInterval(timerId);
            setPaymentLinkValid(false);
            setIsLinkExpired(true);
            return 0;
          }
          return prevTimeLeft - 1;
        });
      }, 1000);
    }

    return () => clearInterval(timerId);
  }, [open, timeLeft]);

  const formatTime = (seconds: number | null) => {
    if (seconds === null) return '00:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(
      remainingSeconds,
    ).padStart(2, '0')}`;
  };

  const handleCopyClick = () => {
    if (!paymentLink) return;
    navigator.clipboard
      .writeText(paymentLink)
      .then(() => {
        enqueueSnackbar('הקישור הועתק בהצלחה!', { variant: 'success' });
      })
      .catch((err) => {
        enqueueSnackbar('אירעה שגיאה בהעתקת הקישור.', { variant: 'error' });
        console.error('Failed to copy link: ', err);
      });
  };

  const shareByEmail = () => {
    if (!paymentLink || !paymentLinkValid) return;
    const subject = encodeURIComponent('לינק לתשלום');
    const body = encodeURIComponent(
      `שלום,\n\nמצורף לינק לתשלום בסך ${formatMoney(Number(payment?.paymentAmount))} ש״ח:\n${paymentLink}\n\nבברכה,\n[שם השולח]`,
    );
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const shareBySMS = () => {
    if (!paymentLink || !paymentLinkValid) return;
    const body = encodeURIComponent(`שלום, מצורף לינק לתשלום: ${paymentLink}`);
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.indexOf('iphone') > -1 || userAgent.indexOf('ipad') > -1) {
      window.open(`sms:&body=${body}`);
    } else {
      window.open(`sms:?body=${body}`);
    }
  };

  const shareByWhatsApp = () => {
    if (!paymentLink || !paymentLinkValid) return;
    const text = encodeURIComponent(
      `שלום,\n\nמצורף לינק לתשלום בסך ${formatMoney(Number(payment?.paymentAmount))} ש״ח:\n${paymentLink}`,
    );
    window.open(`https://wa.me/?text=${text}`);
  };

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: '35px',
        },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: '16px',
          right: '16px',
          zIndex: 1,
        }}
      >
        <img
          alt="close"
          src={'/assets/client/close.svg'}
          width="15"
          height="15"
          style={{ cursor: 'pointer' }}
        />
      </IconButton>

      {loading ? (
        <Box
          sx={{
            width: '500px',
            height: '400px',
            alignItems: 'center',
            justifyContent: 'center',
            display: 'flex',
          }}
        >
          <ReactLoading
            type={'bubbles'}
            color={theme.palette.green.main}
            height={250}
            width={250}
          />
        </Box>
      ) : (
        <Stack
          alignItems="center"
          spacing={4}
          sx={{
            width: '500px',
            '& .MuiDialog-paper': {
              padding: '60px',
              boxSizing: 'border-box',
              position: 'relative',
              overflow: 'hidden',
            },
            px: '40px',
            py: '70px',
          }}
        >
          <Stack>
            <Typography
              textAlign="center"
              fontSize="20px"
              fontWeight="700"
              fontFamily={'AlmoniDL'}
            >
              לינק הוראת תשלום (תקף ל-15 דקות)
            </Typography>
            <Typography
              textAlign="center"
              fontSize="20px"
              fontWeight="700"
              fontFamily={'AlmoniDL'}
            >
              {clientName ? `ל${clientName}` : ''}
            </Typography>
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: '100%' }}
          >
            <Stack
              spacing={1}
              alignItems="flex-end"
              sx={{ width: '230px', pr: 2 }}
            >
              <Typography
                fontSize="14px"
                fontWeight="700"
                fontFamily={'AlmoniDL'}
              >
                :בסכום של
              </Typography>
              <Stack direction="row-reverse" alignItems="center">
                <CoinIcon />
                <Typography
                  fontSize="28px"
                  fontWeight="400"
                  fontFamily={'Montserrat'}
                >
                  {formatMoney(Number(payment?.paymentAmount))}
                </Typography>
              </Stack>
            </Stack>

            <Divider
              orientation="vertical"
              flexItem
              sx={{ backgroundColor: theme.palette.dark?.border || 'black' }}
            />

            <Stack sx={{ width: '230px', pl: 2 }}>
              <FundLogo width="130px" logoIndex={payment?.indexLogo || ''} />
            </Stack>
          </Stack>

          {showLink && paymentLink && paymentLinkValid ? (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={3}
              sx={{
                width: '100%',
                height: '45px',
                boxSizing: 'border-box',
                background: '#F2F4F7',
                borderRadius: '17px',
                p: '12px',
              }}
            >
              <Box
                sx={{
                  background: '#B6F829',
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  cursor: 'pointer',
                }}
                onClick={handleCopyClick}
              >
                <Icon width="23px" icon="ep:copy-document" />
              </Box>
              <Typography
                fontSize="20px"
                fontWeight="700"
                fontFamily="AlmoniDL"
                noWrap
                sx={{
                  textOverflow: 'ellipsis',
                }}
              >
                {paymentLink}
              </Typography>
              <Box
                sx={{ background: '#E3E4E6', borderRadius: '17px', p: '8px' }}
              >
                <Typography
                  fontSize="18px"
                  fontWeight="300"
                  fontFamily={'AlmoniDL'}
                >
                  {formatTime(timeLeft)}
                </Typography>
              </Box>
            </Stack>
          ) : (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
            >
              <Box
                sx={{
                  background: '#7AED20',
                  py: '15px',
                  px: '50px',
                  borderRadius: '17px',
                  cursor: 'pointer',
                }}
                onClick={handleApprove}
              >
                <Typography
                  fontSize="18px"
                  fontWeight="700"
                  fontFamily={'AlmoniDL'}
                >
                  אישור
                </Typography>
              </Box>
              <Box
                sx={{
                  background: '#7AED2033',
                  py: '15px',
                  px: '50px',
                  borderRadius: '17px',
                  cursor: 'pointer',
                }}
                onClick={handleCancel}
              >
                <Typography
                  fontSize="18px"
                  fontWeight="700"
                  fontFamily={'AlmoniDL'}
                >
                  ביטול
                </Typography>
              </Box>
            </Stack>
          )}

          <Stack alignItems="center" spacing={2}>
            <Typography
              fontSize="16px"
              fontWeight="300"
              fontFamily={'AlmoniDL'}
            >
              :שתף
            </Typography>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
            >
              <Stack
                spacing={1}
                direction="row"
                alignItems="center"
                sx={{
                  background:
                    showLink && paymentLinkValid ? '#B6F829' : '#E3E4E6',
                  borderRadius: '16px',
                  px: '16px',
                  py: '8px',
                  cursor:
                    showLink && paymentLinkValid ? 'pointer' : 'not-allowed',
                  opacity: showLink && paymentLinkValid ? 1 : 0.6,
                }}
                onClick={shareByEmail}
              >
                <Typography
                  fontSize="16px"
                  fontWeight="700"
                  fontFamily={'AlmoniDL'}
                >
                  מייל
                </Typography>
                <Icon icon="ic:sharp-alternate-email" />
              </Stack>
              <Stack
                spacing={1}
                direction="row"
                alignItems="center"
                sx={{
                  background:
                    showLink && paymentLinkValid ? '#B6F829' : '#E3E4E6',
                  borderRadius: '16px',
                  px: '16px',
                  py: '8px',
                  cursor:
                    showLink && paymentLinkValid ? 'pointer' : 'not-allowed',
                  opacity: showLink && paymentLinkValid ? 1 : 0.6,
                }}
                onClick={shareBySMS}
              >
                <Typography
                  fontSize="16px"
                  fontWeight="700"
                  fontFamily={'AlmoniDL'}
                >
                  SMS
                </Typography>
                <Icon icon="hugeicons:mail-02" />
              </Stack>
              <Stack
                spacing={1}
                direction="row"
                alignItems="center"
                sx={{
                  background:
                    showLink && paymentLinkValid ? '#B6F829' : '#E3E4E6',
                  borderRadius: '16px',
                  px: '16px',
                  py: '8px',
                  cursor:
                    showLink && paymentLinkValid ? 'pointer' : 'not-allowed',
                  opacity: showLink && paymentLinkValid ? 1 : 0.6,
                }}
                onClick={shareByWhatsApp}
              >
                <Typography
                  fontSize="16px"
                  fontWeight="700"
                  fontFamily={'AlmoniDL'}
                >
                  ווטסאפ
                </Typography>
                <Icon icon="ic:baseline-whatsapp" />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      )}
    </Dialog>
  );
};

export default Link;
